export type DesignCatalogueTemplateId =
  | 'promo_grid_clearance'
  | 'promo_grid_compact'
  | 'promo_grid_clean'
  | 'promo_grid_year_end'
  | 'promo_grid_new_product'
  | 'promo_grid_festive'
  | 'promo_grid_giveaway';

export type DesignCatalogueTheme =
  | 'clearance'
  | 'clean'
  | 'year_end'
  | 'new_product'
  | 'festive'
  | 'giveaway';

/** Cover page composition — not just colour. */
export type DesignCoverLayout =
  | 'stack-bottom' // logo top, title bottom (classic promo)
  | 'center-hero' // logo + title centered
  | 'split-panel' // text panel + colour panel
  | 'banner-top' // solid top band + white lower
  | 'side-rail' // vertical brand rail + title
  | 'stamp'; // big circular badge / giveaway

/** Content-page chrome. */
export type DesignPageChrome =
  | 'top-bar'
  | 'colour-band'
  | 'side-rail'
  | 'minimal'
  | 'footer-only';

/** Product card arrangement. */
export type DesignCardLayout =
  | 'photo-top' // classic grid cards
  | 'photo-left' // horizontal list rows
  | 'ticket' // strip / ticket rows
  | 'feature-mix'; // 1 large + smaller grid

export type DesignCatalogueTemplateDef = {
  id: DesignCatalogueTemplateId;
  label: string;
  description: string;
  defaultSubtitle: string;
  itemsPerPage: number;
  theme: DesignCatalogueTheme;
  columns: 1 | 2 | 3;
  coverLayout: DesignCoverLayout;
  pageChrome: DesignPageChrome;
  cardLayout: DesignCardLayout;
  /** Short layout hint shown in gallery (e.g. "2×2 grid"). */
  layoutHint: string;
  swatchFrom: string;
  swatchTo: string;
};

export const DESIGN_CATALOGUE_TEMPLATES: DesignCatalogueTemplateDef[] = [
  {
    id: 'promo_grid_clearance',
    label: 'Clearance',
    description: 'Full-bleed orange cover, title at bottom. Change Columns/Rows for 2×2 or denser grids.',
    defaultSubtitle: 'Clearance · Special Price',
    itemsPerPage: 4,
    theme: 'clearance',
    columns: 2,
    coverLayout: 'stack-bottom',
    pageChrome: 'top-bar',
    cardLayout: 'photo-top',
    layoutHint: 'Cover bottom · photo cards',
    swatchFrom: '#9a3412',
    swatchTo: '#fbbf24',
  },
  {
    id: 'promo_grid_clean',
    label: 'Product catalogue (clean)',
    description: 'Split cover + horizontal photo-left rows — brochure / price list feel.',
    defaultSubtitle: 'Product Highlights',
    itemsPerPage: 5,
    theme: 'clean',
    columns: 1,
    coverLayout: 'split-panel',
    pageChrome: 'minimal',
    cardLayout: 'photo-left',
    layoutHint: 'Split cover · list rows',
    swatchFrom: '#0f172a',
    swatchTo: '#334155',
  },
  {
    id: 'promo_grid_new_product',
    label: 'New product launch',
    description: 'Centred hero cover + colour header band; one featured item + grid.',
    defaultSubtitle: 'Just Arrived · New Products',
    itemsPerPage: 5,
    theme: 'new_product',
    columns: 2,
    coverLayout: 'center-hero',
    pageChrome: 'colour-band',
    cardLayout: 'feature-mix',
    layoutHint: 'Centre cover · feature + grid',
    swatchFrom: '#0f766e',
    swatchTo: '#5eead4',
  },
  {
    id: 'promo_grid_festive',
    label: 'Festive celebration',
    description: 'Full festive gradient cover + gold header product pages. Stable A4 layout.',
    defaultSubtitle: 'Festive Specials',
    itemsPerPage: 4,
    theme: 'festive',
    columns: 2,
    coverLayout: 'stack-bottom',
    pageChrome: 'colour-band',
    cardLayout: 'photo-top',
    layoutHint: 'Festive cover · gold header',
    swatchFrom: '#9f1239',
    swatchTo: '#eab308',
  },
  {
    id: 'promo_grid_year_end',
    label: 'Year-end promo',
    description: 'Top banner cover (title on colour band) + dark header product pages.',
    defaultSubtitle: 'Year-End Specials',
    itemsPerPage: 4,
    theme: 'year_end',
    columns: 2,
    coverLayout: 'banner-top',
    pageChrome: 'colour-band',
    cardLayout: 'photo-top',
    layoutHint: 'Banner cover · dark header',
    swatchFrom: '#7f1d1d',
    swatchTo: '#fbbf24',
  },
  {
    id: 'promo_grid_giveaway',
    label: 'Giveaway / free gift',
    description: 'Stamp-style cover + ticket-strip product rows.',
    defaultSubtitle: 'Free Gift · Giveaway',
    itemsPerPage: 6,
    theme: 'giveaway',
    columns: 1,
    coverLayout: 'stamp',
    pageChrome: 'footer-only',
    cardLayout: 'ticket',
    layoutHint: 'Stamp cover · ticket rows',
    swatchFrom: '#166534',
    swatchTo: '#bef264',
  },
];

/** Legacy id kept for saved catalogues — maps to Clearance. */
const LEGACY_TEMPLATE_ALIASES: Record<string, DesignCatalogueTemplateId> = {
  promo_grid_compact: 'promo_grid_clearance',
};

export function designTemplateById(id: string): DesignCatalogueTemplateDef {
  const resolved = LEGACY_TEMPLATE_ALIASES[id] ?? id;
  return (
    DESIGN_CATALOGUE_TEMPLATES.find((t) => t.id === resolved) ?? DESIGN_CATALOGUE_TEMPLATES[0]!
  );
}

export type DesignLayoutGrid = {
  columns: 1 | 2 | 3;
  rows: number;
  itemsPerPage: number;
};

/** Max grid that fits A4 when cards shrink into equal row tracks. */
export function maxLayoutColumns(template: DesignCatalogueTemplateDef): 1 | 2 | 3 {
  if (template.cardLayout === 'photo-left' || template.cardLayout === 'ticket') return 1;
  if (template.cardLayout === 'feature-mix') return 2;
  return 3;
}

export function maxLayoutRows(template: DesignCatalogueTemplateDef): number {
  switch (template.cardLayout) {
    case 'photo-left':
      return 5;
    case 'ticket':
      return 6;
    case 'feature-mix':
      // Hero row + (rows-1) nested product rows
      return 3;
    case 'photo-top':
    default:
      // Cards shrink into equal A4 tracks — 3 rows is fine for 2–3 columns.
      return 3;
  }
}

export function defaultLayoutRows(template: DesignCatalogueTemplateDef): number {
  if (template.cardLayout === 'feature-mix') {
    // itemsPerPage ≈ 1 hero + (rows-1)*cols → solve rows from template default
    const nested = Math.max(0, template.itemsPerPage - 1);
    const nestedRows = Math.max(1, Math.ceil(nested / Math.max(1, template.columns)));
    return Math.min(1 + nestedRows, maxLayoutRows(template));
  }
  const fromTemplate = Math.max(1, Math.ceil(template.itemsPerPage / Math.max(1, template.columns)));
  return Math.min(fromTemplate, maxLayoutRows(template));
}

/** Resolve effective grid — project overrides win over template defaults, capped to A4-safe max. */
export function resolveDesignLayoutGrid(
  template: DesignCatalogueTemplateDef,
  overrides?: { columns?: number | null; rows?: number | null },
): DesignLayoutGrid {
  const maxCols = maxLayoutColumns(template);
  const maxRows = maxLayoutRows(template);
  let columns = (overrides?.columns ?? template.columns) as number;
  let rows = overrides?.rows ?? defaultLayoutRows(template);

  if (template.cardLayout === 'photo-left' || template.cardLayout === 'ticket') {
    columns = 1;
  }

  columns = Math.min(maxCols, Math.max(1, Math.round(columns))) as 1 | 2 | 3;
  rows = Math.min(maxRows, Math.max(1, Math.round(rows)));

  // Feature-mix: 1 hero + remaining slots in a (rows-1) × columns nested grid.
  // Naive cols×rows would be 6 for 2×3 and leave a dead nested cell.
  const itemsPerPage =
    template.cardLayout === 'feature-mix'
      ? 1 + Math.max(0, rows - 1) * columns
      : columns * rows;

  return {
    columns: columns as 1 | 2 | 3,
    rows,
    itemsPerPage,
  };
}
